import { useEffect, useState } from 'react';
import axios from 'axios';

import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { createAuthHeaders } from '@/lib/api-client';
import { creditsApi } from '@/lib/api/credits';
import { auth } from '@/lib/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ReferralData {
  credits_awarded: number;
  bonus_shown?: boolean;
}

export function ReferralBonusModal() {
  const [open, setOpen] = useState(false);
  const [bonusCredits, setBonusCredits] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);

  useEffect(() => {
    checkReferralBonus();
  }, []);

  async function checkReferralBonus() {
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: createAuthHeaders(token),
      });

      const referral: ReferralData | null = response.data?.user?.referral;
      if (!referral || referral.credits_awarded <= 0 || referral.bonus_shown) return;

      // Fetch total credit balance
      const details = await creditsApi.getDetails(token);

      setBonusCredits(referral.credits_awarded);
      setTotalCredits(Math.round(details.balance));
      setOpen(true);
    } catch {
      // Not critical
    }
  }

  async function handleDismiss() {
    setOpen(false);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      await axios.post(`${API_URL}/api/auth/referral-bonus-shown`, {}, {
        headers: createAuthHeaders(token),
      });
    } catch {
      // Worst case modal shows again next visit
    }
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="sm:max-w-sm p-0 border-0 shadow-xl overflow-hidden gap-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-purple-600 to-blue-600" />

        <div className="px-6 pt-6 pb-5 space-y-5">
          {/* Header */}
          <div className="space-y-1.5">
            <h2 className="text-lg font-semibold tracking-tight">You've got $50 in free credits!</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              You're starting with {totalCredits} credits — {totalCredits - bonusCredits} free credits that every account gets, plus {bonusCredits} bonus credits on us.
            </p>
          </div>

          {/* Credit breakdown */}
          <div className="rounded-xl bg-muted/40 px-4 py-3.5 space-y-3">
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted-foreground">Free credits</span>
              <div className="flex items-center gap-2 tabular-nums">
                <span>{totalCredits - bonusCredits}</span>
                <span className="text-muted-foreground/60">($25)</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted-foreground">Bonus credits</span>
              <div className="flex items-center gap-2 tabular-nums text-emerald-500">
                <span>+{bonusCredits}</span>
                <span className="text-emerald-500/60">($25)</span>
              </div>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Total</span>
              <span className="text-sm font-semibold tabular-nums">{totalCredits} credits</span>
            </div>
          </div>

          <p className="text-[13px] text-muted-foreground">
            Credits are used when the AI assistant helps you build apps.
          </p>

          {/* CTA */}
          <Button onClick={handleDismiss} className="w-full">
            Start building
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
